import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserCheck, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const DEPTS = ["RH", "Comptabilité & Paie", "Fiscalité", "Direction"];
const ROLE_LABEL = { employe: "Employé", responsable: "Responsable", gerant: "Gérant" };

export default function StaffAdmin({ user }) {
  const [profiles, setProfiles] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/profiles").then((r) => setProfiles(r.data));
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const patch = async (p, body, msg) => {
    setBusyId(p.user_id);
    try {
      await api.patch(`/profiles/${p.user_id}`, body);
      toast.success(msg);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const pending = (profiles || []).filter((p) => !p.active);
  const staff = (profiles || []).filter((p) => p.active);

  return (
    <div className="space-y-9">
      <section data-testid="pending-requests-section">
        <h2 className="font-display text-xl font-bold text-korban-navy">Demandes d'accès</h2>
        {!profiles ? (
          <p className="mt-3 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : pending.length === 0 ? (
          <p className="mt-3 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="pending-empty">
            Aucune demande en attente.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {pending.map((p) => (
              <div key={p.user_id} className="rounded-xl border border-korban-gold/50 bg-[#fffaf0] p-4" data-testid={`pending-row-${p.user_id}`}>
                <p className="font-bold text-korban-navy">{p.full_name}</p>
                <p className="text-xs text-slate-500">{p.email} · département souhaité : <b>{p.dept}</b></p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => patch(p, { active: true, role: "employe" }, "Compte activé (employé).")}
                    disabled={busyId === p.user_id}
                    data-testid={`activate-employe-${p.user_id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-korban-blue px-3.5 py-2 text-xs font-bold text-white hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-50"
                  >
                    <UserCheck size={13} /> Activer (employé)
                  </button>
                  <button
                    onClick={() => patch(p, { active: true, role: "responsable" }, "Compte activé (responsable).")}
                    disabled={busyId === p.user_id}
                    data-testid={`activate-responsable-${p.user_id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-korban-navy px-3.5 py-2 text-xs font-bold text-korban-gold hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-50"
                  >
                    <ShieldCheck size={13} /> Activer (responsable)
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="staff-table-section">
        <h2 className="font-display text-xl font-bold text-korban-navy">Personnel</h2>
        {profiles && staff.length === 0 ? (
          <p className="mt-3 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">Aucun membre actif.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {staff.map((p) => (
              <div key={p.user_id} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`staff-row-${p.user_id}`}>
                <div className="min-w-0">
                  <p className="truncate font-bold text-korban-navy">
                    {p.full_name}
                    {p.user_id === user.user_id && <span className="ml-2 text-xs font-semibold text-korban-blue">(vous)</span>}
                  </p>
                  <p className="truncate text-xs text-slate-500">{p.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={p.dept}
                    onChange={(e) => patch(p, { dept: e.target.value }, "Département mis à jour.")}
                    disabled={busyId === p.user_id}
                    data-testid={`staff-select-dept-${p.user_id}`}
                    className="field field-light !w-auto !py-2 text-xs font-semibold"
                  >
                    {DEPTS.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={p.role}
                    onChange={(e) => patch(p, { role: e.target.value }, "Rôle mis à jour.")}
                    disabled={busyId === p.user_id}
                    data-testid={`staff-select-role-${p.user_id}`}
                    className="field field-light !w-auto !py-2 text-xs font-semibold"
                  >
                    {Object.entries(ROLE_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  {p.user_id !== user.user_id && (
                    <button
                      onClick={() => patch(p, { active: false }, "Compte désactivé.")}
                      disabled={busyId === p.user_id}
                      data-testid={`staff-deactivate-${p.user_id}`}
                      className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-50"
                    >
                      Désactiver
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
